import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'

// @material-ui
import { Grid, Typography, useMediaQuery } from '@material-ui/core'
import { Pagination } from '@material-ui/lab'
import { useTheme } from '@material-ui/core/styles'

// core
import { useGlobal } from '_core/hooks/useGlobal'
import { withLang } from '_core/hocs/withLang'
import { Title } from '_core/components/Title'
import { State } from '_core/components/State'
import { ProductListItem } from '_core/components/ProductListItem'

// style
import { useProductListStyle } from './productListStyle'

const RawProductList = props => {
  const {
    data,
    hasTitle,
    hasPaging,
    hasPagingTop,
    hasPagingBottom,
    initialPage,
    initialRows,
    inRowLg,
    inRowMd,
    inRowSm,
    inRowXs,
    category,
    requestParams,
    renderEmpty,
    renderError,
    emptyMessage,
    failureMessage,
    renderItem,
    ItemComponent,
    langInfo,
    getLangString: l,
  } = props

  const theme = useTheme()
  const classes = useProductListStyle()

  const {
    request: { queue },
    config: {
      API_METHOD,
      DEFAULT_MESSAGE,
      API_ERROR: { DATA_WRONG, DATA_NOT_FOUND },
      REPLACE_CURRENCY
    },
  } = useGlobal()

  // const isXs = useMediaQuery(theme.breakpoints.up('xs'))
  const isSm = useMediaQuery(theme.breakpoints.up('sm'))
  const isMd = useMediaQuery(theme.breakpoints.up('md'))
  const isLg = useMediaQuery(theme.breakpoints.up('lg'))

  const countInRow = isLg ? inRowLg : isMd ? inRowMd : isSm ? inRowSm : inRowXs

  const [nodeKey, setNodeKey] = useState(0)
  const [page, setPage] = useState(initialPage)
  const [limit, setLimit] = useState(initialRows * countInRow)

  useEffect(() => {
    setLimit(initialRows * countInRow)
  }, [countInRow])

  useEffect(() => {
    setNodeKey(nk => nk + 1)
  }, [langInfo, category, page, limit, JSON.stringify(requestParams)])

  const getData = async () => {
    return await queue(API_METHOD.GOODS, {
      currency: REPLACE_CURRENCY.SHOP_GOODS,
      ...requestParams,
      category,
      page,
      limit,
    });
  }

  const renderList = items => {
    const xs = 12 / inRowXs
    const sm = 12 / inRowSm
    const md = 12 / inRowMd
    const lg = 12 / inRowLg

    return items.map((item, index) => (
      <Grid item={true} key={index} xs={xs} sm={sm} md={md} lg={lg}>
        {typeof renderItem === 'function' ? (
          renderItem({ data: item })
        ) : (
          <ItemComponent requestParams={requestParams} data={item} />
        )}
      </Grid>
    ))
  }

  const renderPaging = data =>
    hasPaging && data.hits > limit ? (
      <Pagination
        className={classes.paging}
        classes={{ ul: classes.pagingUl }}
        count={Math.ceil(data.hits / limit)}
        defaultPage={initialPage}
        page={page}
        onChange={(_, newPage) => setPage(newPage)}
        color="primary"
        size={isSm ? 'large' : 'small'}
      />
    ) : null

  const renderEmptyMessage = () =>
    renderEmpty != null ? (
      renderEmpty()
    ) : (
      <Typography align="center" variant="h6" color="textSecondary">
        {emptyMessage != null ? l(emptyMessage) : l('r.nichego.ne.najdeno')}
      </Typography>
    )

  const renderErrorMessage = error =>
    renderError != null ? (
      renderError(error)
    ) : (
      <Typography align="center" variant="h6" color="textSecondary">
        {failureMessage != null ? l(failureMessage) : l(DEFAULT_MESSAGE.ERROR)}
      </Typography>
    )

  const stateProps = data != null ? { data } : { getProps: { data: getData } }

  return (
    <State
      key={nodeKey}
      {...stateProps}
      render={({ data }) => {
        if (data.list?.length) {
          return (
            <>
              {hasTitle ? (
                <Title icon="shop">{l('r._.shop.goods')}</Title>
              ) : null}
              {hasPagingTop ? renderPaging(data) : null}
              <Grid container={true}>{renderList(data.list)}</Grid>
              {hasPagingBottom ? renderPaging(data) : null}
            </>
          )
        }

        return renderEmptyMessage()
      }}
      renderFailure={({ error }) =>
        [DATA_WRONG, DATA_NOT_FOUND].includes(error.code)
          ? renderEmptyMessage()
          : renderErrorMessage(error)
      }
    />
  )
}

RawProductList.defaultProps = {
  initialPage: 1,
  initialRows: 4,
  inRowLg: 4,
  inRowMd: 3,
  inRowSm: 2,
  inRowXs: 1,
  hasTitle: true,
  hasPaging: true,
  hasPagingTop: true,
  hasPagingBottom: true,
  requestParams: {},
  ItemComponent: ProductListItem,
}

RawProductList.propTypes = {
  // self props
  data: PropTypes.object,
  initialPage: PropTypes.number,
  initialRows: PropTypes.number,
  inRowLg: PropTypes.number,
  inRowMd: PropTypes.number,
  inRowSm: PropTypes.number,
  inRowXs: PropTypes.number,
  hasTitle: PropTypes.bool,
  hasPaging: PropTypes.bool,
  hasPagingTop: PropTypes.bool,
  hasPagingBottom: PropTypes.bool,
  requestParams: PropTypes.object,
  renderEmpty: PropTypes.func,
  renderError: PropTypes.func,
  emptyMessage: PropTypes.string,
  failureMessage: PropTypes.string,
  category: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  renderItem: PropTypes.func,
  ItemComponent: PropTypes.elementType,

  // `withLang` HOC props
  langInfo: PropTypes.object,
  langStrings: PropTypes.object,
  getLangObject: PropTypes.func,
  getFirstLangString: PropTypes.func,
  getLangStringSet: PropTypes.func,
  getLangString: PropTypes.func.isRequired,
}

export const ProductList = withLang(RawProductList)
